import axios from '@/lib/axios';
import { unwrapApiResponse } from '@/lib/unwrap-api-response';
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

/** Matches `GET /compliance/issue-reports/booking/{booking_id}/`. */
export interface BookingIssueReport {
  submitted: boolean;
  id?: string;
  report_id?: string;
  booking_id?: string;
  direction?: string;
  category_key?: string;
  issue_key?: string;
  computed_severity?: string;
  details?: string;
  evidence_urls?: string[];
  status?: string;
  created_at?: string;
  updated_at?: string;
}

export const bookingIssueReportQueryKey = (bookingId: string) =>
  ['compliance', 'issue-report', bookingId] as const;

async function getBookingIssueReport(
  bookingId: string
): Promise<BookingIssueReport> {
  const response = await axios.get(
    `/compliance/issue-reports/booking/${bookingId}/`
  );
  return unwrapApiResponse<BookingIssueReport>(response.data);
}

export function useGetBookingIssueReportQuery(
  bookingId: string,
  options?: Omit<
    Partial<UseQueryOptions<BookingIssueReport, Error>>,
    'queryKey' | 'queryFn'
  >
) {
  const { enabled: enabledFromOptions, ...rest } = options ?? {};
  return useQuery({
    queryKey: bookingIssueReportQueryKey(bookingId),
    queryFn: () => getBookingIssueReport(bookingId),
    retry: false,
    enabled:
      !!bookingId &&
      bookingId.trim().length > 0 &&
      (enabledFromOptions ?? true),
    ...rest,
  });
}
